import type { Junction, Road } from '../../shared/types.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';

/**
 * Cutting junctions out of a road centreline.
 *
 * Kerbs, lane markings and street lamps all hang off the centreline, and all three look
 * wrong if they run straight through a crossroads: a raised lip across the mouth of the
 * street you are turning into, a white line over the middle of the junction, a lamppost
 * standing in the road. The generator already tells us where every junction is and how big
 * it is, so the fix is to hand each of them the centreline with the junction intervals
 * removed.
 *
 * Clipping the *centreline* rather than the offset edge is deliberate and slightly
 * conservative: a point at half-width to the side of a centreline point that is `r` from a
 * junction centre is `hypot(r, halfWidth)` away from it, so anything derived from a clipped
 * run is at least as clear of the disc as the run itself.
 */

const CELL = 32;
/** Every disc is registered in the cells its radius plus this reaches, so `pad` is free. */
const MAX_PAD = 8;

function cellKey(cx: number, cz: number): number {
  return ((cx + 32768) << 16) | (cz + 32768);
}

interface Disc {
  x: number;
  z: number;
  r: number;
}

export class JunctionIndex {
  private readonly grid = new Map<number, number[]>();
  private readonly discs: Disc[] = [];

  constructor(junctions: Junction[]) {
    for (const j of junctions) {
      const index = this.discs.length;
      this.discs.push({ x: j.x, z: j.z, r: j.r });
      const reach = j.r + MAX_PAD;
      const minCx = Math.floor((j.x - reach) / CELL);
      const maxCx = Math.floor((j.x + reach) / CELL);
      const minCz = Math.floor((j.z - reach) / CELL);
      const maxCz = Math.floor((j.z + reach) / CELL);
      for (let cx = minCx; cx <= maxCx; cx++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const key = cellKey(cx, cz);
          const bucket = this.grid.get(key);
          if (bucket) bucket.push(index);
          else this.grid.set(key, [index]);
        }
      }
    }
  }

  get size(): number {
    return this.discs.length;
  }

  /**
   * Splits a `[x0,z0, x1,z1, ...]` polyline into the runs that stay clear of every junction
   * disc grown by `pad`. Runs shorter than `minLength` are dropped — a 30 cm sliver of kerb
   * between two junctions is triangles nobody will ever see.
   */
  clear(pts: number[], pad: number, minLength = 2): number[][] {
    const runs: number[][] = [];
    let current: number[] | null = null;
    const n = pts.length / 2;

    for (let i = 1; i < n; i++) {
      const ax = pts[(i - 1) * 2];
      const az = pts[(i - 1) * 2 + 1];
      const bx = pts[i * 2];
      const bz = pts[i * 2 + 1];
      const blocked = this.blockedIntervals(ax, az, bx, bz, pad);

      // Walk the gaps between blocked intervals in order, emitting a piece for each.
      let cursor = 0;
      for (const [t0, t1] of blocked) {
        if (t0 > cursor) {
          current = extend(runs, current, ax, az, bx, bz, cursor, t0);
        }
        cursor = Math.max(cursor, t1);
        // The segment re-enters a junction, so whatever run we were building ends here.
        current = null;
      }
      if (cursor < 1) current = extend(runs, current, ax, az, bx, bz, cursor, 1);
      else current = null;
    }

    return runs.filter((run) => run.length >= 4 && lengthOf(run) >= minLength);
  }

  /**
   * The sub-intervals of segment a→b that fall inside a junction, sorted and merged.
   *
   * Solving |a + t·d − c|² = r² per disc rather than sampling: road vertices can be 100 m
   * apart on a straight, and a sampled test would happily run a kerb through a crossroads
   * that happens to sit between two samples.
   */
  private blockedIntervals(
    ax: number,
    az: number,
    bx: number,
    bz: number,
    pad: number,
  ): [number, number][] {
    const dx = bx - ax;
    const dz = bz - az;
    const len2 = dx * dx + dz * dz;
    if (len2 < 1e-9) return [];

    const spans: [number, number][] = [];
    const seen = new Set<number>();
    const minCx = Math.floor(Math.min(ax, bx) / CELL);
    const maxCx = Math.floor(Math.max(ax, bx) / CELL);
    const minCz = Math.floor(Math.min(az, bz) / CELL);
    const maxCz = Math.floor(Math.max(az, bz) / CELL);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const bucket = this.grid.get(cellKey(cx, cz));
        if (!bucket) continue;
        for (const di of bucket) {
          if (seen.has(di)) continue;
          seen.add(di);
          const disc = this.discs[di];
          const r = disc.r + Math.min(pad, MAX_PAD);
          const fx = ax - disc.x;
          const fz = az - disc.z;
          const b = 2 * (fx * dx + fz * dz);
          const c = fx * fx + fz * fz - r * r;
          const discriminant = b * b - 4 * len2 * c;
          if (discriminant <= 0) continue;
          const root = Math.sqrt(discriminant);
          const t0 = Math.max(0, (-b - root) / (2 * len2));
          const t1 = Math.min(1, (-b + root) / (2 * len2));
          if (t1 > t0) spans.push([t0, t1]);
        }
      }
    }

    if (spans.length < 2) return spans;
    spans.sort((p, q) => p[0] - q[0]);
    const merged: [number, number][] = [spans[0]];
    for (let i = 1; i < spans.length; i++) {
      const last = merged[merged.length - 1];
      if (spans[i][0] <= last[1]) last[1] = Math.max(last[1], spans[i][1]);
      else merged.push(spans[i]);
    }
    return merged;
  }
}

/**
 * Appends the piece of a→b between t0 and t1.
 *
 * Returns the run to carry into the next segment, or null when the piece stopped short of
 * b — the next segment starts somewhere else, so it cannot be the same run.
 */
function extend(
  runs: number[][],
  current: number[] | null,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  t0: number,
  t1: number,
): number[] | null {
  const dx = bx - ax;
  const dz = bz - az;
  let run = current;
  if (!run || t0 > 1e-6) {
    run = [ax + dx * t0, az + dz * t0];
    runs.push(run);
  }
  run.push(ax + dx * t1, az + dz * t1);
  return t1 >= 1 - 1e-6 ? run : null;
}

function lengthOf(run: number[]): number {
  let d = 0;
  for (let i = 2; i < run.length; i += 2) {
    d += Math.hypot(run[i] - run[i - 2], run[i + 1] - run[i - 1]);
  }
  return d;
}

// ---------------------------------------------------------------------------
// other people's tarmac
// ---------------------------------------------------------------------------

/**
 * How close to another street's edge something may stand before it is on that street. A
 * little inside the drawn edge, so a kerb that merely touches the next carriageway is kept.
 */
const OVERLAP_MARGIN = 0.2;

/**
 * Two surfaces further apart than this in height are on different levels: a street under a
 * viaduct is not one the viaduct's kerb can stand in, however much they overlap in plan.
 *
 * Half the headroom a deck is given over a street, so anything stacked is well outside it.
 * Not much less, because the two halves of a dual carriageway are two structures with two
 * ramps, and on the way up the Leszno viaduct they are a metre and a half apart.
 */
const LEVEL_GAP = 2.5;

/** Spacing at which a run is probed for other streets, before the edges are bisected. */
const PROBE_STEP = 2;

/**
 * Whose carriageway a point is on, and at what height.
 *
 * Junction discs only clear decoration out of the junctions a street is *part of*. Where two
 * streets overlap without sharing a node — the two halves of a dual carriageway converging on
 * a roundabout, a slip road merging into an avenue — nothing did, and each painted its centre
 * line and stood its kerb across the other's tarmac. Rondo Zatorze in Leszno had kerb stones
 * lying diagonally across its entries. This is the question all of them now ask instead.
 *
 * Height is part of the question because overlap in plan is not overlap: a viaduct is on top
 * of the street it crosses, and both keep their kerbs.
 */
export class CarriagewayIndex {
  private readonly grid = new Map<number, number[]>();
  private readonly segments: { road: Road; ax: number; az: number; bx: number; bz: number; reach2: number }[] = [];

  constructor(
    roads: Road[],
    private readonly heightOf: (road: Road, x: number, z: number) => number,
  ) {
    for (const road of roads) {
      if (!ROAD_SPECS[road.cls].drivable) continue;
      const reach = Math.max(0, road.width / 2 - OVERLAP_MARGIN);
      for (let i = 2; i < road.pts.length; i += 2) {
        const index = this.segments.length;
        const s = { road, ax: road.pts[i - 2], az: road.pts[i - 1], bx: road.pts[i], bz: road.pts[i + 1], reach2: reach * reach };
        this.segments.push(s);
        for (let cx = Math.floor((Math.min(s.ax, s.bx) - reach) / CELL); cx <= Math.floor((Math.max(s.ax, s.bx) + reach) / CELL); cx++) {
          for (let cz = Math.floor((Math.min(s.az, s.bz) - reach) / CELL); cz <= Math.floor((Math.max(s.az, s.bz) + reach) / CELL); cz++) {
            const key = cellKey(cx, cz);
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  /**
   * True when a drivable street at about height `y` covers a point — any street but the
   * way or ways in `except`, which is -1 to ask about every street there is.
   */
  covers(x: number, z: number, y: number, except: number | ReadonlySet<number>): boolean {
    const bucket = this.grid.get(cellKey(Math.floor(x / CELL), Math.floor(z / CELL)));
    if (!bucket) return false;
    for (const index of bucket) {
      const s = this.segments[index];
      if (typeof except === 'number' ? s.road.id === except : except.has(s.road.id)) continue;
      const vx = s.bx - s.ax;
      const vz = s.bz - s.az;
      const len2 = vx * vx + vz * vz;
      let t = len2 > 1e-9 ? ((x - s.ax) * vx + (z - s.az) * vz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const dx = x - (s.ax + vx * t);
      const dz = z - (s.az + vz * t);
      if (dx * dx + dz * dz > s.reach2) continue;
      if (Math.abs(this.heightOf(s.road, x, z) - y) < LEVEL_GAP) return true;
    }
    return false;
  }
}

/**
 * Splits a polyline into the runs where `blocked` says nothing is in the way.
 *
 * Probed every couple of metres and bisected at each change, rather than solved exactly the
 * way `JunctionIndex.clear` solves its discs, because "inside another street" is a union of
 * capsules at varying heights and the answer only has to be good to a few centimetres. A run
 * that is clear end to end comes back with exactly its own vertices, so a street with nothing
 * beside it costs the triangles it always did.
 */
export function clearRuns(
  pts: number[],
  blocked: (x: number, z: number, ux: number, uz: number) => boolean,
  minLength = 2,
): number[][] {
  const runs: number[][] = [];
  let current: number[] | null = null;
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2];
    const az = pts[i - 1];
    const bx = pts[i];
    const bz = pts[i + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1e-6) continue;
    const ux = (bx - ax) / len;
    const uz = (bz - az) / len;
    const at = (t: number) => blocked(ax + (bx - ax) * t, az + (bz - az) * t, ux, uz);

    const steps = Math.min(64, Math.max(1, Math.ceil(len / PROBE_STEP)));
    let t0 = 0;
    let wasBlocked = at(0);
    let start = wasBlocked ? -1 : 0;
    const spans: [number, number][] = [];
    for (let k = 1; k <= steps; k++) {
      const t1 = k / steps;
      const now = at(t1);
      if (now !== wasBlocked) {
        // Six halvings of a two-metre probe is three centimetres.
        let lo = t0;
        let hi = t1;
        for (let b = 0; b < 6; b++) {
          const mid = (lo + hi) / 2;
          if (at(mid) === wasBlocked) lo = mid;
          else hi = mid;
        }
        const edge = (lo + hi) / 2;
        if (wasBlocked) start = edge;
        else spans.push([start, edge]);
        wasBlocked = now;
      }
      t0 = t1;
    }
    if (!wasBlocked) spans.push([start, 1]);

    for (const [s0, s1] of spans) current = extend(runs, current, ax, az, bx, bz, s0, s1);
    if (!spans.length || spans[spans.length - 1][1] < 1 - 1e-6) current = null;
  }
  return runs.filter((run) => run.length >= 4 && lengthOf(run) >= minLength);
}
